import * as THREE from "three";
import {GLTF, GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader.js";
import {KTX2Loader} from "three/examples/jsm/loaders/KTX2Loader.js";
import {MeshoptDecoder} from "three/examples/jsm/libs/meshopt_decoder.module.js";
import {IsGlassMaterial} from "../../../../Utils/ThreeJS/LightMapUtils.js";
import {store} from "../../../../Store/index.js";

// Runtime side of the Blender-baked lightmap pipeline. (see Scripts/Blender/BlenderExport.py + Optimize.js)
// Each exported node carries `userData.lm` ("LM_<obj>"); its lightmap is at `<webDir>/Lightmaps/<lm>.ktx2`, sampled via uv1 (Blender's "lightmap" UV layer).
// Baked meshes are rendered unlit: MeshBasicMaterial computes `albedo * lightMap * lightMapIntensity / PI`, so intensity = LM_SCALE * PI
// reproduces the Blender preview (`Emission(albedo * lightmap) * LM_SCALE`). Realtime lights don't affect these meshes.

export const bakedWebDir = "/3D/ZUp/LHQ_Web";
/** Must match LM_SCALE in LHQ_lightmap_bake.py (the lightmap PNGs store `light / LM_SCALE`). */
export const LM_SCALE = 4;

/** The Blender sun-lamp the lightmaps were baked with (manifest.json's "sun"; written by BlenderExport.py). Z-up, same space as the baked meshes. */
export type BakedSun = {
	/** Normalized direction the light travels (ie. from the sun toward the scene). */
	dir: [number, number, number],
	/** Linear RGB (Blender's lamp color). */
	color: [number, number, number],
	/** Angular diameter in radians (Blender's "Angle"; shadow softness). */
	angle: number,
};
/** The LHQ_Web manifest.json (written by BlenderExport.py, rewritten by Optimize.js). */
export type BakedManifest = {
	/** Load-group name -> entry. Key order = blend (outliner) order = load order. */
	collections: {[name: string]: {file: string, path?: string[], objects: {[obj: string]: {lm: string|n}}}},
	sun?: BakedSun,
};
let bakedManifestPromise: Promise<BakedManifest|n>|n;
/** Fetched once, and shared by all users (sun direction, load-group list). Resolves to null if the fetch fails. */
export function LoadBakedManifest() {
	bakedManifestPromise ??= fetch(`${bakedWebDir}/manifest.json`).then(a=>a.json() as Promise<BakedManifest>).catch(err=>{
		console.warn("[BakedLightmaps] failed to load manifest.json:", err);
		return null;
	});
	return bakedManifestPromise;
}

export function LoadBakedSun() {
	return LoadBakedManifest().then(manifest=>{
		if (manifest == null) return null;
		const sun = manifest.sun;
		if (sun == null) console.warn("[BakedLightmaps] manifest.json has no sun entry; keeping the hardcoded light direction");
		return sun ?? null;
	});
}

let loaders: {gltf: GLTFLoader, ktx2: KTX2Loader}|n;
function GetLoaders(renderer: THREE.WebGLRenderer) {
	if (loaders == null) {
		// the transcoder files are copied from three's package by rspack.config.mjs
		const ktx2 = new KTX2Loader().setTranscoderPath("/FromNodeModules/basis/").setWorkerLimit(2).detectSupport(renderer);
		const gltf = new GLTFLoader().setKTX2Loader(ktx2).setMeshoptDecoder(MeshoptDecoder);
		loaders = {gltf, ktx2};
	}
	return loaders;
}
/** Shared KTX2 loader (one Basis transcoder worker-pool for the whole LHQ scene; also used by SkyBackground's dome mode). */
export const GetKTX2Loader = (renderer: THREE.WebGLRenderer)=>GetLoaders(renderer).ktx2;

/** Models are loaded one at a time (rather than all in parallel), with their textures uploaded to the GPU before the next starts;
 * this spreads the decode/upload work over time, avoiding the long main-thread stalls that glitch the Quest's compositor. */
let loadQueue = Promise.resolve() as Promise<any>;

export function LoadBakedGLTF(path: string, renderer: THREE.WebGLRenderer): Promise<GLTF> {
	const result = loadQueue.then(()=>LoadBakedGLTF_now(path, renderer));
	loadQueue = result.catch(()=>{}); // a failed model shouldn't block the rest
	return result;
}

const lightmapCache = new Map<string, Promise<THREE.Texture|n>>();
function LoadLightmap(key: string, renderer: THREE.WebGLRenderer) {
	if (!lightmapCache.has(key)) {
		lightmapCache.set(key, GetLoaders(renderer).ktx2.loadAsync(`${bakedWebDir}/Lightmaps/${key}.ktx2`).then(tex=>{
			tex.channel = 1; // uv1 = Blender's "lightmap" uv-layer (TEXCOORD_1)
			tex.colorSpace = THREE.SRGBColorSpace; // lightmaps are stored sRGB-encoded
			// no mipmaps: long/thin objects (eg. the 66x3m balcony floor) get thin islands in a mostly-empty (black) atlas, and at
			// grazing angles the smaller mips average the island with that black => far too dark. Lightmaps are low-frequency, so level-0 + linear doesn't visibly alias.
			tex.minFilter = THREE.LinearFilter;
			tex.needsUpdate = true;
			return tex;
		}, err=>{
			console.warn(`[BakedLightmaps] failed to load lightmap ${key}:`, err);
			return null;
		}));
	}
	return lightmapCache.get(key)!;
}

/** Walks up from the mesh to find the node carrying `userData.lm`. (meshes the glTF-pipeline split per-material are children of the source-object's node) */
function FindLightmapKey(obj: THREE.Object3D) {
	for (let node: THREE.Object3D|n = obj; node != null; node = node.parent) {
		if (typeof node.userData?.lm == "string") return node.userData.lm as string;
	}
	return null;
}

async function LoadBakedGLTF_now(path: string, renderer: THREE.WebGLRenderer) {
	const gltf = await GetLoaders(renderer).gltf.loadAsync(path);

	const meshes = [] as THREE.Mesh[];
	gltf.scene.traverse(node=>{ if ((node as THREE.Mesh).isMesh) meshes.push(node as THREE.Mesh); });
	const textures = new Set<THREE.Texture>();
	for (const mesh of meshes) {
		const lmKey = FindLightmapKey(mesh); // "" = lightmap intentionally disabled (lm_texel_m <= 0 in Blender)
		const lightMap = lmKey ? await LoadLightmap(lmKey, renderer) : null;
		if (lightMap == null && lmKey != "") console.warn(`[BakedLightmaps] no lightmap for mesh "${mesh.name}" in ${path}; leaving it realtime-lit`);

		const convert = (mat: THREE.Material)=>{
			const src = mat as THREE.MeshStandardMaterial;
			// objects without a bake keep their realtime-lit material
			if (lightMap == null) return src;
			// glass gets no lightmap (lightmaps are for the frames only); it's still made unlit, so its look doesn't depend on the (dim) realtime lights
			if (IsGlassMaterial(src)) return ToGlassMaterial(src);
			return ToBasicMaterial(src, lightMap);
		};
		mesh.material = Array.isArray(mesh.material) ? mesh.material.map(convert) : convert(mesh.material);
		for (const mat of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
			const m = mat as THREE.MeshBasicMaterial;
			if (m.map) textures.add(m.map);
			if (m.lightMap) textures.add(m.lightMap);
		}
	}

	// upload now (rather than on first render, where all models' textures would upload in the same frame)
	for (const tex of textures) renderer.initTexture(tex);
	await new Promise(resolve=>setTimeout(resolve, 0)); // yield a frame-slot between models

	return gltf;
}

/** Multiplier on the baked-lightmap intensity (LM_SCALE * PI base); 1 = matches the Blender bake preview. */
export const GetLightmapIntensity = ()=>store.main.tools.vr.scene_lhq.lightmapIntensity;

/** All unlit materials created by ToBasicMaterial that carry a lightmap, so the intensity setting can be re-applied at runtime.
 * Cleared at scene-mount (ClearBakedLightmapMats); this intentionally leaves the previous scene's materials to GC rather than tracking per-mount generations. */
const bakedLightmapMats = [] as THREE.MeshBasicMaterial[];
/** Drops all previously-tracked materials from the registry; the scene teardown (or GC) handles disposing of the materials themselves. */
export const ClearBakedLightmapMats = ()=>bakedLightmapMats.length = 0;
const TrackBakedLightmapMat = (mat: THREE.MeshBasicMaterial)=>bakedLightmapMats.push(mat);

/** Re-applies the lightmapIntensity setting to every tracked baked material. */
export function ApplyLightmapIntensity() {
	const intensity = GetLightmapIntensity();
	for (const mat of bakedLightmapMats) {
		if (mat.lightMap == null) continue;
		mat.lightMapIntensity = LM_SCALE * Math.PI * intensity;
	}
}

// ---------- glass: unlit tint + fresnel-weighted sky reflection ----------
// Blender side: glass materials are Principled with a flat dark tint, low alpha (~0.12), metallic 0, roughness ~0.03 (exported as baseColorFactor/alpha).
// Here: output = mix(tint, sky, R) with R = schlick(R0 -> 1 at grazing angles); alpha is raised by R, so reflections stay visible over the (mostly clear) glass.

/** Reflectance at normal incidence (~0.04 for real glass; a bit higher reads better in VR). */
const GLASS_R0 = 0.06;

const glassMats = [] as THREE.MeshBasicMaterial[];
let glassEnvMap: THREE.Texture|n;
let glassEnvRotY = 0;
/** Called by SkyBackground once the sky is loaded (or null on unmount); applies to existing + future glass materials. */
export function SetGlassEnvMap(tex: THREE.Texture|n, rotY: number) {
	glassEnvMap = tex;
	glassEnvRotY = rotY;
	for (const mat of glassMats) ApplyGlassEnvMap(mat);
}
function ApplyGlassEnvMap(mat: THREE.MeshBasicMaterial) {
	mat.envMap = glassEnvMap ?? null;
	mat.envMapRotation.set(0, glassEnvRotY, 0); // same convention as scene.backgroundRotation
	mat.needsUpdate = true;
}

function ToGlassMaterial(src: THREE.MeshStandardMaterial) {
	const result = ToBasicMaterial(src, null);
	result.transparent = true;
	result.depthWrite = false;
	result.side = THREE.DoubleSide;
	result.combine = THREE.MixOperation;
	result.onBeforeCompile = shader=>{
		shader.uniforms.glassR0 = {value: GLASS_R0};
		shader.vertexShader = shader.vertexShader
			.replace("#include <common>", "#include <common>\nvarying float vGlassFresnel;")
			.replace("#include <fog_vertex>", `#include <fog_vertex>
				{
					vec4 gWorldPos = modelMatrix * vec4(transformed, 1.0);
					vec3 gN = normalize(mat3(modelMatrix) * normal);
					vec3 gV = normalize(gWorldPos.xyz - cameraPosition);
					vGlassFresnel = pow(1.0 - abs(dot(gN, gV)), 5.0);
				}`);
		shader.fragmentShader = shader.fragmentShader
			.replace("#include <common>", "#include <common>\nvarying float vGlassFresnel;\nuniform float glassR0;")
			.replace("#include <envmap_fragment>", `
				float glassR = mix(glassR0, 1.0, vGlassFresnel);
				#define reflectivity glassR
				#include <envmap_fragment>
				#undef reflectivity
				#ifdef USE_ENVMAP
					diffuseColor.a = mix(diffuseColor.a, 1.0, glassR);
				#endif`);
	};
	result.customProgramCacheKey = ()=>"lhq-glass";
	glassMats.push(result);
	ApplyGlassEnvMap(result);
	return result;
}

function ToBasicMaterial(src: THREE.MeshStandardMaterial, lightMap: THREE.Texture|n) {
	const result = new THREE.MeshBasicMaterial({
		name: src.name,
		color: src.color,
		map: src.map,
		alphaMap: src.alphaMap,
		transparent: src.transparent,
		opacity: src.opacity,
		alphaTest: src.alphaTest,
		side: src.side,
		depthWrite: src.depthWrite,
		vertexColors: src.vertexColors,
	});
	if (lightMap) {
		result.lightMap = lightMap;
		result.lightMapIntensity = LM_SCALE * Math.PI * GetLightmapIntensity();
		TrackBakedLightmapMat(result);
	}
	if (src.name.startsWith("BookSpines")) ApplyTintMask(result);
	src.dispose();
	return result;
}

/** Procedural books (Scripts/Blender/LHQ/ProceduralBooks.py): the atlas' alpha is a tint-mask, not opacity.
 * final = texRGB * mix(1, vertexColor, texAlpha)  (so gold bands/page-edges keep their color; cloth gets the per-book color) */
function ApplyTintMask(mat: THREE.MeshBasicMaterial) {
	mat.vertexColors = true;
	mat.transparent = false;
	mat.alphaTest = 0;
	mat.onBeforeCompile = shader=>{
		shader.fragmentShader = shader.fragmentShader.replace("#include <color_fragment>", `
			#if defined(USE_MAP) && defined(USE_COLOR)
				diffuseColor.rgb *= mix(vec3(1.0), vColor.rgb, sampledDiffuseColor.a);
				diffuseColor.a = opacity;
			#else
				#include <color_fragment>
			#endif
		`);
	};
	mat.customProgramCacheKey = ()=>"BookSpinesTintMask";
}
